// Shared MyTools API calls — extracted from MyToolsPage.tsx so other features
// (e.g. claudeagents' "Publish to Marketplace" and the skill-share resolve flow)
// can drive the same upsert/publish/install endpoints without importing a
// feature-private module.

const API_BASE = process.env.REACT_APP_Auth_Url || '';

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
  };
}

export interface UpsertMyToolConfigFieldSchema {
  key: string;
  label: string;
  type: 'text' | 'toggle' | 'select';
  options?: string[];
  requiredFromInstaller: boolean;
  hint?: string;
  scope?: 'shared' | 'per_user';
}

export interface UpsertMyToolPayload {
  id?: number;
  type: number;
  name: string;
  category: string;
  icon: string;
  description: string;
  skills: string;
  kbFileNames: string[];
  visibility: number;
  mcpUrl?: string;
  mcpHeadersTemplate?: Record<string, string>[];
  configSchema?: UpsertMyToolConfigFieldSchema[];
  skillContent?: string;
  agentShareToken?: string;
  // Skill type only, optional — the originating Digital Workforce skill's own
  // stable id, so a later share attempt for the same live skill can find and
  // reuse this row instead of minting a duplicate. See SkillShareMenuModal.
  sourceSkillId?: string;
}

/** The upsert endpoint rejects invalid saves with a specific reason in the body
 * (`{ success: false, message: "Skill content is required" }` and similar for a
 * missing MCP URL or an installer-supplied header carrying a stored value).
 * Reporting only the status code threw all of that away and left the user with
 * "Failed to save tool (400)" and nothing to act on. */
async function saveToolError(res: Response): Promise<Error> {
  let message = '';
  try {
    const body = await res.json();
    message = typeof body?.message === 'string' ? body.message : '';
  } catch {
    // Not JSON, or an empty body - fall back to the status below.
  }
  return new Error(message || `Failed to save tool (${res.status})`);
}

export async function upsertMyTool(payload: UpsertMyToolPayload): Promise<string> {
  const res = await fetch(`${API_BASE}/api/MyTools/upsert`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await saveToolError(res);
  const json = await res.json();
  return String(json.id ?? payload.id ?? '');
}

/** Same as upsertMyTool, but also returns the Python-side skill_id the save produced
 * (needed to attach a freshly-minted skill copy to an agent during Share Agent import). */
export async function upsertMyToolWithSkillId(payload: UpsertMyToolPayload): Promise<{ id: string; skillId: string }> {
  const res = await fetch(`${API_BASE}/api/MyTools/upsert`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await saveToolError(res);
  const json = await res.json();
  return { id: String(json.id ?? payload.id ?? ''), skillId: String(json.skillId ?? '') };
}

/** Looks up whether the current user already shared this live Digital Workforce
 * skill before — returns the existing tool's id, or null if this would be the
 * first share. */
export async function findToolBySourceSkillId(sourceSkillId: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/MyTools/by-source-skill/${encodeURIComponent(sourceSkillId)}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to look up existing share (${res.status})`);
  const json = await res.json();
  return json?.id != null ? String(json.id) : null;
}

export interface MyToolSummary {
  id: string;
  name: string;
  type: 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
  visibility: 'just_me' | 'my_agency' | 'published';
  publishStatus?: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  isAgencyInstalled?: boolean;
  // Full enough to safely round-trip through upsertMyTool (e.g. to change just
  // visibility) without a partial upsert clobbering the rest of the row — the
  // backend UPDATE overwrites every one of these columns unconditionally, so a
  // caller must always send back every field it got, not just the one it changed.
  category: string;
  icon: string;
  description: string;
  skills: string;
  kbFileNames: string[];
  mcpUrl?: string;
  mcpHeadersTemplate?: Record<string, string>[];
  configSchema?: UpsertMyToolConfigFieldSchema[];
  skillContent?: string;
  agentShareToken?: string;
}

const MYTOOL_INT_TO_TYPE: Record<number, MyToolSummary['type']> = {
  0: 'skill', 1: 'agent', 2: 'workflow', 3: 'widget', 4: 'mcp_server', 5: 'integration',
};
const MYTOOL_INT_TO_VISIBILITY: Record<number, MyToolSummary['visibility']> = {
  0: 'just_me', 1: 'my_agency', 2: 'published',
};

/** Fetches a tool the caller already knows the id of (e.g. a Digital Workforce
 * MCP connection's linked My Creations listing) — enough to both drive
 * ToolShareMenuModal and safely round-trip through upsertMyTool. */
export async function getMyTool(toolId: string): Promise<MyToolSummary | null> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}`, { headers: authHeaders() });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load tool (${res.status})`);
  const json = await res.json();
  const data = json?.data ?? {};
  let kbFileNames: string[] = [];
  if (typeof data.kbFileNames === 'string') {
    try { kbFileNames = JSON.parse(data.kbFileNames); } catch { /* ignore */ }
  } else if (Array.isArray(data.kbFileNames)) {
    kbFileNames = data.kbFileNames;
  }
  return {
    id: String(data.id),
    name: data.name ?? '',
    type: MYTOOL_INT_TO_TYPE[data.type] ?? 'skill',
    visibility: MYTOOL_INT_TO_VISIBILITY[data.visibility] ?? 'just_me',
    publishStatus: data.publishStatus ?? undefined,
    isAgencyInstalled: !!data.isAgencyInstalled,
    category: data.category ?? '',
    icon: data.icon ?? '',
    description: data.description ?? '',
    skills: data.skills ?? '',
    kbFileNames,
    mcpUrl: data.mcpUrl ?? undefined,
    mcpHeadersTemplate: data.mcpHeadersTemplateList ?? undefined,
    configSchema: data.configSchemaList ?? undefined,
    skillContent: data.skillContent ?? undefined,
    agentShareToken: data.agentShareToken ?? undefined,
  };
}

export interface AgentToolRef {
  id: string;
  isAgencyInstalled: boolean;
}

/** The caller's existing my_tools row for an agent, matched through any of the
 * agent's active share tokens (a my_tools row only stores its share token, not
 * the agent id). Prefers one already installed for the agency. Lets Digital
 * Workforce's Install for Agency reuse the same row instead of minting a new
 * one per click - a new row means a new tool id, which defeats the duplicate check. */
export async function findAgentTool(shareTokens: string[]): Promise<AgentToolRef | null> {
  if (shareTokens.length === 0) return null;
  const res = await fetch(`${API_BASE}/api/MyTools/my`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to load your tools (${res.status})`);
  const json = await res.json();
  const tokens = new Set(shareTokens);
  const matches = ((json?.data ?? []) as { id: number; type: number; agentShareToken?: string; isAgencyInstalled?: boolean }[])
    .filter(t => t.type === 1 && !!t.agentShareToken && tokens.has(t.agentShareToken));
  const match = matches.find(t => t.isAgencyInstalled) ?? matches[0];
  return match ? { id: String(match.id), isAgencyInstalled: !!match.isAgencyInstalled } : null;
}

export async function submitPublishRequest(toolId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/publish-request`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (res.status === 409) throw new Error('already_pending');
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { message?: string }).message || 'Failed to submit for review');
  }
}

export async function installTool(toolId: string, mcpFieldValues?: Record<string, string>, source?: 'marketplace' | 'share_link'): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/install`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ toolId: Number(toolId), mcpFieldValues, source }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 409) throw new Error('already_installed');
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to install tool (${res.status})`);
  }
}

// Agency Admin only — installs a tool for every active user in the company.
export async function uninstallToolForAgency(toolId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/uninstall-for-agency`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to uninstall for agency (${res.status})`);
}

export async function installToolForAgency(toolId: string, mcpFieldValues?: Record<string, string>): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/install-for-agency`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ mcpFieldValues }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to install for agency (${res.status})`);
  }
}

export interface AgentAgencyInstallResult {
  user_id: string;
  // pending_setup: in the agency but not set up yet (setup runs on its own at their
  // next sign-in) - they get the agent automatically once it has. owner: the
  // sharer, who keeps the original.
  status?: 'installed' | 'owner' | 'pending_setup' | 'error';
  agent_id?: string;
  attached_mcps?: { name: string; url: string }[];
  needs_setup_mcps?: { name: string; url: string }[];
  error?: string;
}

export interface AgentAgencyInstallResponse {
  results: AgentAgencyInstallResult[];
  alreadyInstalled: number;
}

export interface AgentAgencyInstallSummary {
  created: number;
  pendingSetup: number;
  failed: number;
  needsMcpSetup: number;
  alreadyInstalled: number;
}

export function summarizeAgentAgencyInstall({ results, alreadyInstalled }: AgentAgencyInstallResponse): AgentAgencyInstallSummary {
  const created = results.filter(r => r.status === 'installed');
  return {
    created: created.length,
    pendingSetup: results.filter(r => r.status === 'pending_setup').length,
    failed: results.filter(r => r.status === 'error').length,
    needsMcpSetup: created.filter(r => (r.needs_setup_mcps?.length ?? 0) > 0).length,
    alreadyInstalled,
  };
}

// Agency Admin only — creates a brand-new agent (independent copy, never a shared
// agent_id) for every active user in the company from a shared agent's snapshot.
export async function installAgentForAgency(toolId: string): Promise<AgentAgencyInstallResponse> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/install-agent-for-agency`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || "We couldn't install this agent for your agency. Please try again.");
  }
  return {
    results: (body.results ?? []) as AgentAgencyInstallResult[],
    alreadyInstalled: Number(body.alreadyInstalled ?? 0),
  };
}

// GET /api/MyTools/installed also backfills anything installed for the user's agency
// that they don't have yet (agents included). Call it right after Digital Workforce
// setup so agency-installed agents show up immediately. Best-effort - never throws.
export async function syncAgencyInstalls(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/MyTools/installed`, { headers: authHeaders() });
  } catch {
    // The My Tools page runs the same sync on its next load.
  }
}

export type ShareScope = 'agency' | 'anyone';

export interface Share {
  id: string;
  token: string;
  scope: ShareScope;
  isActive: boolean;
  createdAt: string;
  revokedAt?: string;
}

export interface ResolvedShare {
  copiedToolId: string;
  toolType: 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
  toolName: string;
  toolDescription: string;
  toolIcon: string;
  toolCategory: string;
  mcpUrl?: string;
  mcpHeaderKeys: string[];
  isAgencyOwner: boolean;
}

const RESOLVE_INT_TO_TOOL_TYPE: Record<number, ResolvedShare['toolType']> = {
  0: 'skill', 1: 'agent', 2: 'workflow', 3: 'widget', 4: 'mcp_server', 5: 'integration',
};

export async function createShare(toolId: string, scope: ShareScope): Promise<{ id: string; token: string; scope: ShareScope }> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/share`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ scope }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to create share link (${res.status})`);
  }
  return { id: String(body.id), token: body.token, scope: body.scope };
}

export async function getShares(toolId: string): Promise<Share[]> {
  const res = await fetch(`${API_BASE}/api/MyTools/${toolId}/shares`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to load shares (${res.status})`);
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  return list.map((s: any): Share => ({
    id: String(s.id),
    token: s.token,
    scope: s.scope,
    isActive: s.isActive,
    createdAt: s.createdAt,
    revokedAt: s.revokedAt ?? undefined,
  }));
}

export async function revokeShare(shareId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/${shareId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to revoke share (${res.status})`);
  }
}

export async function regenerateShare(shareId: string): Promise<{ id: string; token: string }> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/${shareId}/regenerate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to regenerate share (${res.status})`);
  }
  return { id: String(body.id), token: body.token };
}

export async function refreshShare(shareId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/${shareId}/refresh`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to refresh share (${res.status})`);
  }
}

export async function resolveShareToken(token: string): Promise<ResolvedShare> {
  const res = await fetch(`${API_BASE}/api/MyTools/shares/resolve/${token}`, { headers: authHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error((body as { message?: string }).message || `Failed to resolve share link (${res.status})`);
  }
  const data = body.data ?? {};
  return {
    copiedToolId: String(data.copiedToolId),
    toolType: RESOLVE_INT_TO_TOOL_TYPE[data.toolType] ?? 'skill',
    toolName: data.toolName ?? '',
    toolDescription: data.toolDescription ?? '',
    toolIcon: data.toolIcon ?? '',
    toolCategory: data.toolCategory ?? '',
    mcpUrl: data.mcpUrl ?? undefined,
    mcpHeaderKeys: Array.isArray(data.mcpHeaderKeys) ? data.mcpHeaderKeys : [],
    isAgencyOwner: !!data.isAgencyOwner,
  };
}
